import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusChip from "../components/StatusChip";
import PhotoInput from "../components/PhotoInput";
import { Plus, Upload, Download, Search, Pencil, Archive as ArchiveIcon, Eye, RotateCcw, Trash2 } from "lucide-react";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";
const lbl = "text-[10px] font-semibold uppercase tracking-wider text-slate-500";

const bLabel = (b) => (b ? (b.dealer_code ? `${b.dealer_code} — ${b.name}` : b.name) : "");

const EMPTY = { reg_no: "", vin: "", model: "", variant: "", fuel_type: "Petrol", color: "", current_odometer: "",
  branch_id: "", induction_date: "", purchase_value: "", insurance_expiry: "", puc_expiry: "",
  service_due_km: "", service_due_date: "", status: "available", photo: null };

function VehicleForm({ vehicle, branches, models, fuels, isAdmin, defaultBranch, onClose, onSaved }) {
  const editing = !!vehicle;
  const [f, setF] = useState(editing ? { ...EMPTY, ...vehicle, purchase_value: vehicle.purchase_value ?? "" } : { ...EMPTY, branch_id: defaultBranch || "" });
  const [odoReason, setOdoReason] = useState("");
  const [newModel, setNewModel] = useState("");
  const [showNewModel, setShowNewModel] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const odoChanged = editing && parseInt(f.current_odometer) !== vehicle.current_odometer;

  const addModel = async () => {
    try {
      const r = await api.post("/vehicle-models", { name: newModel });
      toast.success("Model added");
      setF({ ...f, model: newModel });
      setShowNewModel(false);
      onSaved(r.data.models, true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    const payload = { ...f, current_odometer: parseInt(f.current_odometer),
      purchase_value: f.purchase_value === "" || f.purchase_value == null ? null : parseFloat(f.purchase_value),
      service_due_km: f.service_due_km ? parseInt(f.service_due_km) : null };
    ["induction_date", "insurance_expiry", "puc_expiry", "service_due_date"].forEach((k) => { if (!payload[k]) payload[k] = null; });
    try {
      if (editing) {
        await api.put(`/vehicles/${vehicle.id}/master`, { ...payload, odometer_reason: odoReason || null });
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehicle added — it is now live on the branch board");
      }
      onClose(); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
      <div data-testid="vehicle-form-modal" className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-bold text-[#0E2240]">{editing ? `Edit ${vehicle.reg_no}` : "Add Vehicle"}</h2>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div><label className={lbl}>Registration *</label>
            <input data-testid="vm-reg-input" value={f.reg_no} onChange={(e) => setF({ ...f, reg_no: e.target.value.toUpperCase() })} placeholder="KL-07-XX-0000" className={inputCls} /></div>
          <div><label className={lbl}>VIN (17 chars) *</label>
            <input data-testid="vm-vin-input" value={f.vin} onChange={(e) => setF({ ...f, vin: e.target.value.toUpperCase() })} placeholder="17-char chassis no" className={inputCls} /></div>
          <div><label className={lbl}>Model *</label>
            <select data-testid="vm-model-select" value={f.model} onChange={(e) => e.target.value === "__new" ? setShowNewModel(true) : setF({ ...f, model: e.target.value })} className={inputCls}>
              <option value="">Select model…</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
              {isAdmin && <option value="__new">+ Add new model…</option>}
            </select></div>
          <div><label className={lbl}>Variant</label>
            <input data-testid="vm-variant-input" value={f.variant} onChange={set("variant")} placeholder="e.g. HTX" className={inputCls} /></div>
          {showNewModel && (
            <div className="col-span-2 flex gap-2">
              <input data-testid="vm-new-model-input" value={newModel} onChange={(e) => setNewModel(e.target.value)} placeholder="New model name" className={inputCls} />
              <button data-testid="vm-new-model-save" onClick={addModel} className="mt-1 rounded-xl bg-[#0E2240] px-4 text-xs font-bold text-white">Add</button>
            </div>
          )}
          <div><label className={lbl}>Fuel type *</label>
            <select data-testid="vm-fuel-select" value={f.fuel_type} onChange={set("fuel_type")} className={inputCls}>
              {fuels.map((x) => <option key={x} value={x}>{x}</option>)}
            </select></div>
          <div><label className={lbl}>Color</label>
            <input value={f.color} onChange={set("color")} className={inputCls} /></div>
          <div><label className={lbl}>Odometer (km) *</label>
            <input data-testid="vm-odo-input" type="number" value={f.current_odometer} onChange={set("current_odometer")} className={inputCls} /></div>
          <div><label className={lbl}>Purchase value (₹)</label>
            <input data-testid="vm-purchase-input" type="number" value={f.purchase_value} onChange={set("purchase_value")} placeholder="e.g. 1250000" className={inputCls} /></div>
          {odoChanged && (
            <div className="col-span-2"><label className={`${lbl} text-[#BB162B]`}>Odometer correction reason *</label>
              <input data-testid="vm-odo-reason-input" value={odoReason} onChange={(e) => setOdoReason(e.target.value)} placeholder="Why is the reading being corrected? (audit-logged)" className={inputCls} /></div>
          )}
          <div><label className={lbl}>Branch *</label>
            <select data-testid="vm-branch-select" value={f.branch_id} onChange={set("branch_id")} disabled={editing || !isAdmin} className={`${inputCls} disabled:bg-slate-100 disabled:text-slate-400`}>
              <option value="">Select branch…</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{bLabel(b)}</option>)}
            </select>
            {editing && <p className="mt-1 text-[10px] text-slate-400">Branch moves go through Inter-Branch Transfers</p>}</div>
          <div><label className={lbl}>Owner SM</label>
            <input value={editing ? (vehicle.owner_sm_name || "Auto from branch") : "Auto-filled from branch"} disabled className={`${inputCls} bg-slate-100 text-slate-400`} /></div>
          <div><label className={lbl}>Induction date</label>
            <input type="date" value={f.induction_date || ""} onChange={set("induction_date")} className={inputCls} /></div>
          <div><label className={lbl}>Status</label>
            <select data-testid="vm-status-select" value={f.status} onChange={set("status")} className={inputCls}>
              <option value="available">Available</option><option value="grounded">Grounded</option>
            </select></div>
          <div><label className={lbl}>Insurance expiry</label>
            <input type="date" value={f.insurance_expiry || ""} onChange={set("insurance_expiry")} className={inputCls} /></div>
          <div><label className={lbl}>PUC expiry</label>
            <input type="date" value={f.puc_expiry || ""} onChange={set("puc_expiry")} className={inputCls} /></div>
          <div><label className={lbl}>Service due (km)</label>
            <input type="number" value={f.service_due_km || ""} onChange={set("service_due_km")} className={inputCls} /></div>
          <div><label className={lbl}>Service due (date)</label>
            <input type="date" value={f.service_due_date || ""} onChange={set("service_due_date")} className={inputCls} /></div>
        </div>
        <div className="mt-3"><PhotoInput label="vehicle photo (optional)" value={f.photo} onChange={(p) => setF({ ...f, photo: p })} testId="vm-photo-btn" /></div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-500">Cancel</button>
          <button data-testid="vm-save-btn" onClick={save} className="rounded-xl bg-[#BB162B] py-3 text-sm font-bold text-white">{editing ? "Save Changes" : "Add Vehicle"}</button>
        </div>
      </div>
    </div>
  );
}

function RetireModal({ vehicle, onClose, onDone }) {
  const [reason, setReason] = useState("accident_writeoff");
  const [note, setNote] = useState("");
  const retire = async () => {
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/retire`, { reason, note });
      toast.success(`${vehicle.reg_no} retired · net lifetime cost ₹${(r.data.lifetime.net_lifetime_cost || 0).toLocaleString("en-IN")}`);
      onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div data-testid="retire-modal" className="w-full max-w-sm rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-bold text-[#BB162B]">Retire {vehicle.reg_no}</h2>
        <p className="mt-1 text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5">Exceptional path only (e.g. total-loss write-off). The normal exit is <b>Hand over to Accounts</b> → sale, raised by the branch SM.</p>
        <label className={`${lbl} mt-3 block`}>Reason</label>
        <select data-testid="retire-reason-select" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls}>
          {[["accident_writeoff", "Accident write-off"], ["buyback", "Buyback"], ["retail_conversion", "Retail conversion"], ["returned", "Returned"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="retire-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note…" className={inputCls} />
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-500">Cancel</button>
          <button data-testid="retire-confirm-btn" onClick={retire} className="rounded-xl bg-[#BB162B] py-3 text-sm font-bold text-white">Retire Vehicle</button>
        </div>
      </div>
    </div>
  );
}

function ImportPanel({ onImported, enabled }) {
  const [fileB64, setFileB64] = useState(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const downloadTemplate = async () => {
    const r = await api.get("/vehicle-master/import-template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "TDFMS_Vehicle_Master_Template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };
  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setPreview(null); setReport(null); setBusy(true);
    const rd = new FileReader();
    rd.onload = async () => {
      const b64 = rd.result.split(",")[1];
      setFileB64(b64);
      try {
        const r = await api.post("/vehicle-master/import", { file_base64: b64, confirm: false });
        setPreview(r.data);
      } catch (err) { toast.error(errMsg(err)); }
      setBusy(false);
    };
    rd.readAsDataURL(file);
    e.target.value = "";
  };
  const confirmImport = async () => {
    setBusy(true);
    try {
      const r = await api.post("/vehicle-master/import", { file_base64: fileB64, confirm: true });
      setReport(r.data); setPreview(null);
      toast.success(`${r.data.imported} vehicles imported — live on their branch boards`);
      onImported();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const downloadReport = (data) => {
    const lines = ["Result,Row,Registration/Column,Detail"];
    (data.imported_regs || data.clean_regs || []).forEach((r) => lines.push(`IMPORTED,,${r},`));
    data.errors.forEach((e) => lines.push(`REJECTED,${e.row},"${e.column}","${e.problem.replace(/"/g, "'")}"`));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tdfms_import_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  if (!enabled) {
    return (
      <div data-testid="import-disabled-msg" className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
        Bulk import is currently disabled by Admin. Add or edit vehicles directly in Vehicle Master.
      </div>
    );
  }
  const ErrTable = ({ errors }) => (
    <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-red-100">
      <table className="w-full text-left text-[11px]">
        <thead className="bg-red-50 text-[9px] uppercase text-[#BB162B]"><tr><th className="p-1.5">Row</th><th className="p-1.5">Column</th><th className="p-1.5">Problem</th></tr></thead>
        <tbody>{errors.map((e, i) => <tr key={i} className="border-t border-red-50"><td className="p-1.5 font-bold">{e.row}</td><td className="p-1.5">{e.column}</td><td className="p-1.5 text-slate-600">{e.problem}</td></tr>)}</tbody>
      </table>
    </div>
  );
  return (
    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="font-heading text-sm font-bold text-[#0E2240]">Bulk import (.xlsx — sheet "Vehicle Master")</h3>
      <p className="mt-1 text-[11px] text-slate-500">Create-only and duplicate-safe: existing reg numbers/VINs are always rejected, never edited. Locations matched by <b>Dealer Code</b>. Rows marked "SAMPLE ROW" are skipped.</p>
      <div className="mt-3 flex gap-2 flex-wrap">
        <button data-testid="import-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-[#0E2240]"><Download size={14} /> Download template</button>
        <label className="flex cursor-pointer items-center gap-2 rounded-full bg-[#0E2240] px-4 py-2 text-xs font-bold text-white">
          <Upload size={14} /> {busy ? "Validating…" : "Upload .xlsx"}
          <input data-testid="import-file-input" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={pick} />
        </label>
      </div>
      {preview && (
        <div data-testid="import-preview" className="mt-3 rounded-xl bg-slate-50 p-3 text-xs">
          <div className="font-bold text-[#0E2240]">{fileName}: {preview.total_rows} rows scanned · <span className="text-emerald-700">{preview.clean} clean</span> · <span className="text-[#BB162B]">{preview.failed} with errors</span></div>
          {preview.errors.length > 0 && <ErrTable errors={preview.errors} />}
          <div className="mt-3 flex gap-2">
            <button data-testid="import-confirm-btn" onClick={confirmImport} disabled={busy || preview.clean === 0}
              className="flex-1 rounded-xl bg-[#BB162B] py-2.5 text-sm font-bold text-white disabled:opacity-40">Import {preview.clean} clean rows</button>
            <button onClick={() => setPreview(null)} className="rounded-xl border border-slate-200 px-4 text-xs text-slate-500">Cancel</button>
          </div>
        </div>
      )}
      {report && (
        <div data-testid="import-result" className="mt-3 rounded-xl bg-slate-50 p-3 text-xs">
          <div className="font-bold text-emerald-700">{report.imported} imported</div>
          {report.failed > 0 && <div className="font-bold text-[#BB162B] mt-1">{report.failed} rejected — fix and re-upload (re-uploads are safe)</div>}
          {report.errors.length > 0 && <ErrTable errors={report.errors} />}
          <button data-testid="import-report-btn" onClick={() => downloadReport(report)} className="mt-2 flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-bold text-[#0E2240]"><Download size={12} /> Download import report</button>
        </div>
      )}
    </div>
  );
}

export default function VehicleMaster() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [vehicles, setVehicles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [models, setModels] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [cfg, setCfg] = useState({});
  const [flt, setFlt] = useState({ branch_id: "", model: "", fuel_type: "", status: "", q: "" });
  const [form, setForm] = useState(null); // null | "new" | vehicle
  const [retiring, setRetiring] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const load = () => api.get("/vehicle-master", { params: Object.fromEntries(Object.entries(flt).filter(([, v]) => v)) }).then((r) => setVehicles(r.data)).catch(() => {});
  const reinstate = async (v) => {
    if (!window.confirm(`Reinstate ${v.reg_no}? It returns to the fleet as Available.`)) return;
    try { await api.post(`/vehicles/${v.id}/reinstate`); toast.success(`${v.reg_no} is back in the fleet`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const removeForever = async (v) => {
    if (!window.confirm(`Permanently delete ${v.reg_no}?\n\nThis erases the vehicle and ALL its history (trips, bookings, fuel, workshop, accounts). The registration can then be added again as new. This cannot be undone.`)) return;
    try { const r = await api.delete(`/vehicles/${v.id}`); toast.success(`${r.data.reg_no} deleted permanently`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [flt]);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data));
    api.get("/vehicle-models").then((r) => { setModels(r.data.models); setFuels(r.data.fuel_types); });
    api.get("/config").then((r) => setCfg(r.data));
  }, []);

  if (!["admin", "tdc"].includes(user.role)) return <div className="p-8 text-sm text-slate-400">Vehicle Master is available to Admin and TDC only.</div>;
  const canAdd = isAdmin || (user.role === "tdc" && cfg.tdc_can_add_vehicles);
  const selCls = "rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium";

  return (
    <div className="px-4 py-5 lg:px-8 max-w-5xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Vehicle Master</h1>
          <p className="text-xs text-slate-500 mt-0.5">{vehicles.length} vehicles{user.role === "tdc" && " · your branch"}</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && <button data-testid="vm-import-toggle-btn" onClick={() => setShowImport(!showImport)} className="flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2.5 text-xs font-bold text-[#0E2240]"><Upload size={14} /> Bulk Import</button>}
          {canAdd && <button data-testid="vm-add-btn" onClick={() => setForm("new")} className="flex items-center gap-1.5 rounded-full bg-[#BB162B] px-4 py-2.5 text-sm font-bold text-white"><Plus size={16} /> Add Vehicle</button>}
        </div>
      </div>

      {showImport && isAdmin && <ImportPanel onImported={load} enabled={cfg.bulk_import_enabled !== false} />}

      <div className="mt-4 flex gap-2 flex-wrap items-center">
        {isAdmin && (
          <select data-testid="vm-filter-branch" value={flt.branch_id} onChange={(e) => setFlt({ ...flt, branch_id: e.target.value })} className={selCls}>
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{bLabel(b)}</option>)}
          </select>
        )}
        <select data-testid="vm-filter-model" value={flt.model} onChange={(e) => setFlt({ ...flt, model: e.target.value })} className={selCls}>
          <option value="">All models</option>
          {models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select data-testid="vm-filter-fuel" value={flt.fuel_type} onChange={(e) => setFlt({ ...flt, fuel_type: e.target.value })} className={selCls}>
          <option value="">All fuels</option>
          {fuels.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select data-testid="vm-filter-status" value={flt.status} onChange={(e) => setFlt({ ...flt, status: e.target.value })} className={selCls}>
          <option value="">All statuses</option>
          {["available", "blocked", "on_test_drive", "workshop", "grounded", "in_transit", "with_accounts", "sold", "retired"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input data-testid="vm-search-input" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} placeholder="Reg no or VIN…"
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-8 pr-3 text-xs" />
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {vehicles.map((v) => (
          <div key={v.id} data-testid={`vm-row-${v.reg_no}`} className="rounded-xl border border-slate-200 bg-white p-3.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading font-bold text-[#0E2240]">{v.reg_no}</span>
                <StatusChip status={v.status} />
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{v.model} {v.variant} · {v.fuel_type} · {v.color} · {bLabel(branches.find((b) => b.id === (v.current_branch_id || v.branch_id))) || v.branch_id}</div>
              <div className="text-[10px] text-slate-400">VIN {v.vin} · {(v.current_odometer || 0).toLocaleString("en-IN")} km{v.purchase_value ? ` · ₹${v.purchase_value.toLocaleString("en-IN")}` : ""}{v.induction_date ? ` · inducted ${v.induction_date}` : ""}</div>
            </div>
            <div className="flex gap-1.5">
              <button data-testid={`vm-view-${v.reg_no}`} onClick={() => nav(`/vehicle/${v.id}`)} className="rounded-lg border border-slate-200 p-2 text-slate-500"><Eye size={14} /></button>
              {!["sold", "retired"].includes(v.status) && (
                <button data-testid={`vm-edit-${v.reg_no}`} onClick={() => setForm(v)} className="rounded-lg border border-slate-200 p-2 text-[#0E2240]"><Pencil size={14} /></button>
              )}
              {isAdmin && !["sold", "retired", "on_test_drive", "in_transit"].includes(v.status) && (
                <button data-testid={`vm-retire-${v.reg_no}`} onClick={() => setRetiring(v)} className="rounded-lg border border-red-200 p-2 text-[#BB162B]"><ArchiveIcon size={14} /></button>
              )}
              {isAdmin && v.status === "retired" && (
                <>
                  <button data-testid={`vm-reinstate-${v.reg_no}`} onClick={() => reinstate(v)} title="Reinstate to fleet" className="flex items-center gap-1 rounded-lg border border-emerald-200 px-2 py-2 text-[11px] font-bold text-emerald-700"><RotateCcw size={14} /> Reinstate</button>
                  <button data-testid={`vm-delete-${v.reg_no}`} onClick={() => removeForever(v)} title="Delete permanently" className="flex items-center gap-1 rounded-lg border border-red-200 px-2 py-2 text-[11px] font-bold text-[#BB162B]"><Trash2 size={14} /> Delete</button>
                </>
              )}
            </div>
          </div>
        ))}
        {vehicles.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">No vehicles match — add one with “+ Add Vehicle”</div>}
      </div>

      {form && <VehicleForm vehicle={form === "new" ? null : form} branches={branches} models={models} fuels={fuels}
        isAdmin={isAdmin} defaultBranch={user.branch_id} onClose={() => setForm(null)}
        onSaved={(newModels, keepOpen) => { if (newModels) setModels(newModels); if (!keepOpen) load(); }} />}
      {retiring && <RetireModal vehicle={retiring} onClose={() => setRetiring(null)} onDone={load} />}
    </div>
  );
}
